import { ref } from 'vue'
import { router } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import { walletConfig, walletModal, connectors } from './WalletConnectConfig/walletConfig'
import { abiDefault } from './WalletConnectConfig/abiDefault'
import { bytecode } from './WalletConnectConfig/PortexPledge_bytecode.js'
import { useChat } from './useChat';
import { ENV } from '../../../../utils/useConfig';
import {
    getAccount,
    reconnect,
    getBlockNumber,
    deployContract,
    waitForTransactionReceipt,
    getChainId,
    watchConnections,
    signMessage,
} from '@wagmi/core';

export const USER_ACTION = {
    IDLE: 'idle',
    PENDING: 'pending',
    ACCEPTED: 'accepted',
    REJECTED: 'rejected'
}

const userAddress = ref(null);
const isConnectedWallet = ref(false);
const walletStatus = ref(null);
const transactionHash = ref(null);
const isDeploying = ref(false);
const userAction = ref(USER_ACTION.IDLE);
const block_number = ref(null);
const chain_id = ref(null);

const envParams = {
    current_chain: ENV.current_chain,
    oracle_module: ENV.oracle_module.toLowerCase(),
    oracle_fee: ENV.oracle_fee,
    expiration_buffer: ENV.expiration_buffer,
    gas_limit: ENV.gas_limit,
    app_id: "1",
    beneficiary: ENV.beneficiary,
}

export function WalletConnect() {

    async function reconnectWallet() {
        try {
            if (verifyWalletConnection()) {
                await reconnect(walletConfig, { connectors: connectors });
            }
        } catch (error) {
            console.error('Error reconnecting wallet:', error);
        }
    }

    reconnectWallet();

    async function initializeWallet() {
        try {
            if (await verifyConnectionStatus()) {
                const result = getAccount(walletConfig);
                userAddress.value = result.address;
                block_number.value = await getBlockNumberGpt();
                chain_id.value = getChainId(walletConfig);
            }
        } catch (error) {
            console.error('Error initializing wallet:', error);
        }
    }

    const unwatch = watchConnections(walletConfig, {
        onChange(data) {
            walletStatus.value = data;
        },
    });

    function getUserAccount() {
        const { selectedNetworkId } = walletModal.getState();
        if (selectedNetworkId) {
            const account = getAccount(walletConfig);
            return account;
        } else {
            return null;
        }
    }

    function verifyWalletConnection() {
        const account = getAccount(walletConfig)
        if (account.status === 'connected') {
            isConnectedWallet.value = true;
            userAddress.value = account.address;
            return true;
        } else {
            isConnectedWallet.value = false;
            return false;
        }
    }

    async function verifyConnectionStatus() {
        const account = getAccount(walletConfig)
        switch (account.status) {
            case 'connecting':
                return true;
            case 'reconnecting':
                return true;
            case 'connected':
                return true;
            case 'disconnected':
                return false;
            default:
                return false;
        }
    }

    async function disconnectWallet() {
        await walletModal.disconnect();
    }

    async function switchNetwork() {
        const { ready } = useChat();
        if (!verifyWalletConnection() || !walletStatus.value?.length > 0) return;
        if (walletStatus.value?.[0]?.chainId?.toString() !== envParams.current_chain) {
            await signMessage(walletConfig, {
                message: `Your current wallet ${walletStatus.value[0].chainId.toString()} was not available to deploy new contracts. Please, change your network to ${envParams.current_chain} and click the reset button to create pledges again.`,
            });
            walletModal.open({ view: 'Networks' });
            userAction.value = USER_ACTION.PENDING;
            ready.value = false;
        }
        await walletModal.close();
        userAction.value = USER_ACTION.IDLE;
        ready.value = true;
    }

    async function getBlockNumberGpt() {
        try {
            const chainId = getChainId(walletConfig);
            const blockNumber = await getBlockNumber(walletConfig, {
                chainId: chainId,
            });
            block_number.value = Number(blockNumber);
            return block_number.value;
        } catch (error) {
            console.error('Error getting block number:', error);
            return null;
        }
    }

    async function openMobileWallet() {
        await walletModal.open();
    }

    async function redirectDeeplink() {
        walletModal.redirect({ view: 'AllWallets' });
    }

    async function sendMessageToWallet(text) {
        await signMessage(walletConfig, { message: text });
    }

    async function createContract(commitmentValues) {
        if (isDeploying.value) {
            await signMessage(walletConfig, { message: "Contract deployment is already in progress..." });
            userAction.value = USER_ACTION.REJECTED;
            return;
        }

        isDeploying.value = true;

        const { 
            pledgeAppId,
            showEndMessage,
            reloadData,
            showMessage,
            ready,
        } = useChat();

        if (walletStatus.value?.[0]?.chainId?.toString() !== envParams.current_chain) {
            await switchNetwork();
            if (walletStatus.value?.[0]?.chainId?.toString() !== envParams.current_chain) {
                showMessage({msg: 'Please switch your wallet network to ' + envParams.current_chain + ' and try again.', success: false, history: commitmentValues.history});
                isDeploying.value = false;
                userAction.value = USER_ACTION.PENDING;
                ready.value = false;
                return;
            }
        }

        const amounts = await getAmounts(commitmentValues.pledge_amount, commitmentValues.pledge_currency);

        const result = await deployContract(walletConfig, {
            abi: abiDefault,
            args: [
                commitmentValues.deadline_timestamp,
                envParams.expiration_buffer,
                commitmentValues.beneficiary,
                envParams.oracle_module,
                envParams.app_id,
                envParams.oracle_fee,
            ],
            bytecode: bytecode,
            gas: envParams.gas_limit,
            value: amounts.wei,
        }).catch((error) => {
            console.error(error);
            if (error.message?.includes('User rejected the request')) {
                userAction.value = USER_ACTION.REJECTED;
            } else {
                userAction.value = USER_ACTION.IDLE;
            }
            handleError(
                { path: 'deployContract', domain_type: 'Pledge', domain_id: commitmentValues.authUser },
                `Error Deploy contract, Error: ${error.message}`,
                { auth_user: commitmentValues.authUser, user_wallet_address: userAddress.value, chatId: commitmentValues.chatId, error, commitmentValues: commitmentValues, chainId: envParams.current_chain }
            );
        }).finally(() => {
            isDeploying.value = false;
        });

        if (result) {
            userAction.value = USER_ACTION.ACCEPTED;
            transactionHash.value = result;

            const endMessage = {
                end_message: commitmentValues.end_message,
                history: commitmentValues.history,
                transaction_hash: transactionHash.value,
            };
            await showEndMessage(endMessage);

            const transactionReceipt = await waitForTransactionReceipt(walletConfig, {
                chainId: Number(envParams.current_chain),
                hash: result,
            }).catch((error) => {
                console.error("Transaction failed or timed out:", error);
                showMessage({msg: 'Failed to get Transaction Hash of the contract.', success: false, history: commitmentValues.history});
                return {
                    contractAddress: null,
                    from: null,
                    blockNumber: null,
                };
            });

            const creationTxBlock = transactionReceipt.blockNumber ? Number(transactionReceipt.blockNumber).toString() : null;
            const creationTxTimestamp = Date.now();

            const contractData = {
                creation_tx_id: transactionHash.value ?? transactionReceipt.transactionHash,
                user_email: commitmentValues.authUser,
                script: commitmentValues.script,
                app_id: pledgeAppId.value,
                chatId: commitmentValues.chatId,
                oracle_fee: envParams.oracle_fee,
                chain_id: envParams.current_chain,
                beneficiary: commitmentValues.beneficiary ?? null,
                beneficiary_name: commitmentValues.beneficiary_name ?? null,
                deadline_timestamp: commitmentValues.deadline_timestamp ?? null,
                goal: commitmentValues.pledge_goal ?? null,
                goal_unit: commitmentValues.goal_unit ?? null,
                amount_native: amounts.amount_native ?? null,
                amount_usd: amounts.amount_usd ?? null,
                ticker: amounts.currency ?? 'ETH',
                contract_address: transactionReceipt.contractAddress ?? null,
                creation_tx_block: creationTxBlock ?? null,
                creation_tx_timestamp: creationTxTimestamp.toString() ?? null,
                deployer_address: transactionReceipt.from ?? userAddress.value ?? null,
                transaction_status_code: transactionReceipt.status === 1 ? 1 : 2,
                transaction_status_message: transactionReceipt.status === 1 ? 'Transaction confirmed' : 'Transaction pending',
            };

            router.post(route('main.createPledge'), { data: contractData }, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: (data) => {
                    if (data.props.response?.success) {
                        showEndMessage({
                            end_message: data.props.response.message,
                            history: commitmentValues.history,
                            transaction_hash: transactionHash.value,
                        });
                    } else {
                        showMessage({
                            success: false,
                            msg: data.props.response.message,
                            history: commitmentValues.history,
                        });
                    }
                    reloadData();
                },
                onError: (errors) => {
                    console.error('Error saving pledge data:', errors);
                    handleError(
                        { path: 'createContract', domain_type: 'Pledges', domain_id: transactionHash.value },
                        `Error saving pledge data: ${JSON.stringify(errors)}`,
                        { tx_receipt: transactionHash.value, error: errors, pledge_values: contractData }
                    );
                },
            });
        } else {
            isDeploying.value = false;
        }
    }

    function handleError(context = {}, message = 'Error', data = {}) {
        if (message) {
            console.log('Error: ', message);
        }
        router.post(route('admin.log_manager.store'), {
            data: {
                path: context.path || window.location.pathname,
                domain_type: context.domain_type || 'Unknown',
                domain_id: context.domain_id || 'Unknown',
                content: JSON.stringify(data),
                log_message: message || 'Error',
            },
        }, {
            onSuccess: (data) => {
                console.log('Error logged to server:', data);
            },
            onError: (error) => {
                console.error('Failed to log error to server:', error);
            },
        });
    }

    async function getAmounts(amount, currency) {
        const url = 'https://api.kraken.com/0/public/Spread?pair=XETHZUSD';
        const response = await fetch(url)
        const data = await response.json()
        const eth_price = data.result.XETHZUSD[data.result.XETHZUSD.length - 1][2];
        const result = {
            currency: 'ETH',
            amount_native: 0,
            amount_usd: 0,
            wei: 0
        };

        if (currency === 'ETH') {
            result.amount_native = amount;
            result.amount_usd = (amount * eth_price).toFixed(5);
            result.wei = convertETHtoWei(amount);
        }

        if (currency === 'USD') {
            result.amount_native = (amount / eth_price).toFixed(5);
            result.amount_usd = amount;
            result.wei = convertETHtoWei(result.amount_native);
        }

        return result;
    }

    function convertETHtoWei(amount) {
        return BigInt(parseInt(amount * 10 ** 18));
    }

    return {
        walletModal,
        userAddress,
        userAction,
        isConnectedWallet,
        walletStatus,
        transactionHash,
        USER_ACTION,
        envParams,
        block_number,
        chain_id,
        verifyWalletConnection,
        getBlockNumberGpt,
        createContract,
        getUserAccount,
        disconnectWallet,
        unwatch,
        handleError,
        sendMessageToWallet,
        switchNetwork,
        initializeWallet,
        reconnectWallet,
        redirectDeeplink,
        openMobileWallet,
    }
}
